import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { decodeImageWithMediaKey } from '../utils/whatsappImageDecoder';
import WhatsAppImageModal from './WhatsAppImageModal';

const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
  max-width: 100%;
  margin: 8px 0;
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f0f0;
`;

const LoadingIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #666;
  font-size: 12px;
  z-index: 2;
`;

const ImageInfo = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
  font-size: 11px;
  color: #667781;
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #ffebee;
  border-radius: 8px;
  color: #c62828;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RetryButton = styled.button`
  background: #00a884;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background: #008f73;
  }
`;

const WhatsAppImage = ({ imageData, isOwn }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [errorHandled, setErrorHandled] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [imgSrc, setImgSrc] = useState(imageData?.url);
  const [showModal, setShowModal] = useState(false);
  const imgRef = useRef(null);

  if (!imageData) {
    return null;
  }

  // Timeout de segurança - marcar como carregado após 2 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!imageLoaded && !imageError) {
        setImageLoaded(true);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [imageLoaded, imageError]);

  // Verificar se a imagem já está carregada
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setImageLoaded(true);
    }
  }, [imgSrc]);

  const {
    url,
    mediaKey,
    mimetype,
    width,
    height,
    fileLength,
    caption
  } = imageData;

  // Calcular tamanho em KB/MB
  const fileSizeKB = Math.round(parseInt(fileLength) / 1024);
  const fileSizeText = fileSizeKB > 1024 ? 
    `${Math.round(fileSizeKB / 1024)} MB` : 
    `${fileSizeKB} KB`;

  // Determinar tamanho da imagem para exibição (máximo 300px de largura)
  const maxWidth = 300;
  const aspectRatio = height / width;
  const displayWidth = Math.min(width, maxWidth);
  const displayHeight = Math.round(displayWidth * aspectRatio);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = async () => {
    if (errorHandled) return;

    const maxAttempts = 3;
    
    // Se ainda não tentamos todas as estratégias, tentar novamente
    if (retryAttempt < maxAttempts) {
      const newAttempt = retryAttempt + 1;
      setRetryAttempt(newAttempt);
      
      // Estratégias alternativas
      let newUrl = url;
      
      if (retryAttempt === 0) {
        // Tentar com timestamp para forçar reload
        newUrl = url + (url.includes('?') ? '&t=' + Date.now() : '?t=' + Date.now());
      } else if (retryAttempt === 1) {
        // Tentar através do proxy do backend
        const messageId = imageData.messageId || 'unknown';
        newUrl = `/api/whatsapp/image/${messageId}?url=${encodeURIComponent(url)}`;
      } else if (retryAttempt === 2 && mediaKey && mediaKey.trim()) {
        // Tentar decodificar usando mediaKey
        try {
          const decodedUrl = await decodeImageWithMediaKey(url, mediaKey, mimetype);
          setImgSrc(decodedUrl);
          return;
        } catch (err) {
          // Erro silencioso
        }
      }
      
      setTimeout(() => {
        setImgSrc(newUrl);
      }, 100);
      return;
    }

    // Marcar como processado para evitar loops
    setErrorHandled(true);
    setImageError(true);
  };

  const handleRetry = () => {
    setImageError(false);
    setErrorHandled(false);
    setRetryAttempt(0);
    setImageLoaded(false);
    setImgSrc(url);
  };

  const handleImageClick = () => {
    if (imageLoaded && !imageError) {
      setShowModal(true);
    }
  };

  return (
    <>
      <ImageContainer>
        {imageError ? (
          <ErrorMessage>
            <span>Erro ao carregar imagem</span>
            <RetryButton onClick={handleRetry}>Tentar novamente</RetryButton>
          </ErrorMessage>
        ) : (
          <>
            <ImageWrapper style={{ cursor: imageLoaded && !imageError ? 'pointer' : 'default' }}>
              {!imageLoaded && (
                <LoadingIndicator>Carregando...</LoadingIndicator>
              )}
              <img
                ref={imgRef}
                src={imgSrc}
                alt={caption || "Imagem do WhatsApp"}
                onLoad={handleImageLoad}
                onError={handleImageError}
                onClick={handleImageClick}
                style={{
                  display: 'block',
                  maxWidth: '300px',
                  borderRadius: '8px',
                  opacity: imageLoaded ? 1 : 1,
                  width: displayWidth,
                  height: displayHeight,
                  objectFit: 'contain',
                  transition: 'opacity 0.3s ease',
                  cursor: imageLoaded && !imageError ? 'pointer' : 'default'
                }}
              />
            </ImageWrapper>
            <ImageInfo>
              <span>{fileSizeText}</span>
              <span>{width} × {height}</span>
            </ImageInfo>
            {caption && (
              <div style={{ marginTop: '8px', fontSize: '14px', color: '#111b21' }}>
                {caption}
              </div>
            )}
          </>
        )}
      </ImageContainer>

      {showModal && (
        <WhatsAppImageModal
          imageUrl={imgSrc}
          caption={caption}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default WhatsAppImage;

